import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AsyncState } from '@/components/async';
import { ErrorState } from '@/components/feedback';
import { PublicPageHeading } from '@/components/public/PublicPageHeading';
import {
  PublicUnitGroupList,
  PublicUnitGroupListSkeleton,
  buildAvailableUnitGroups,
} from '@/components/public/units';
import { PublicPageSection } from '@/layouts/PublicPageLayout';
import { AvailabilitiesMapList } from '@/routes/publicProperties/components/AvailabilitiesMapList';
import {
  AvailabilitiesViewToggle,
  type AvailabilitiesViewMode,
} from '@/routes/publicProperties/components/AvailabilitiesViewToggle';
import type { PublicPropertySummary } from '@/lib/types/publicProperty';
import type { AvailabilitiesPageContent } from '@/lib/types/siteSettings';

type AvailabilityContent = {
  eyebrow: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaHref: string;
};

type AvailabilityLabels = {
  immediateLabel: string;
  negotiableLabel: string;
};

export function AvailabilitySection({
  content,
  availabilitiesContent,
  availabilityLabels,
  viewMode,
  onViewModeChange,
  data,
  isLoading,
  error,
  emptyFallback,
  errorMessage,
}: {
  content: AvailabilityContent;
  availabilitiesContent: AvailabilitiesPageContent;
  availabilityLabels: AvailabilityLabels;
  viewMode: AvailabilitiesViewMode;
  onViewModeChange: (next: AvailabilitiesViewMode) => void;
  data: { items: PublicPropertySummary[] } | null | undefined;
  isLoading: boolean;
  error: unknown;
  emptyFallback: ReactNode;
  errorMessage: string;
}): JSX.Element {
  return (
    <PublicPageSection tone="muted" aria-label="Featured availability" id="units">
      <PublicPageHeading
        eyebrow={content.eyebrow}
        title={content.title}
        description={content.description}
        layout="split"
        actions={
          <Link
            to={content.ctaHref || '/availabilities'}
            className="inline-flex min-h-11 items-center justify-center rounded-full border border-brand-accent px-5 py-2 text-sm font-semibold text-brand-accent transition hover:border-brand-primary hover:bg-brand-primary/10 hover:text-brand-primary"
          >
            {content.ctaLabel}
          </Link>
        }
      />

      <div className="mt-6 flex flex-col gap-6">
        <div className="flex justify-start sm:justify-end">
          <AvailabilitiesViewToggle
            value={viewMode}
            onChange={onViewModeChange}
            className="w-full sm:w-auto"
            labels={availabilitiesContent.viewToggle}
          />
        </div>

        <AsyncState
          data={data ?? null}
          isLoading={isLoading}
          error={error}
          skeleton={<PublicUnitGroupListSkeleton />}
          errorFallback={<ErrorState description={errorMessage} />}
          emptyFallback={emptyFallback}
        >
          {(result) => {
            const groups = buildAvailableUnitGroups(result.items);
            if (groups.length === 0) {
              return emptyFallback;
            }
            return viewMode === 'map' ? (
              <AvailabilitiesMapList
                groups={groups}
                content={{
                  mapPanel: availabilitiesContent.mapPanel,
                  mapUnitRow: availabilitiesContent.mapUnitRow,
                  quickView: availabilitiesContent.quickView,
                  availabilityLabels,
                }}
              />
            ) : (
              <PublicUnitGroupList groups={groups} />
            );
          }}
        </AsyncState>
      </div>
    </PublicPageSection>
  );
}
